import React from 'react';
import { Play, Pause, FileText, Upload, CheckCircle, File } from 'lucide-react';
import { Link } from 'react-router-dom';

const DataImportHelpPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background-secondary">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">Data Import Management Help</h1>
          <p className="text-lg text-neutral-600">
            Complete guide to using the Data Import Management system
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Getting Started */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">🚀 Getting Started</h2>
              <div className="space-y-4 text-neutral-700">
                <p>
                  The Data Import Management system allows you to create, configure, and monitor data sources 
                  for importing various types of data into your system. Follow these steps to get started:
                </p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li><strong>Create a Data Source:</strong> Click &quot;Add New Source&quot; to configure a new data source</li>
                  <li><strong>Upload Files:</strong> Upload your data files (CSV, Excel, JSON, TXT) to the data source</li>
                  <li><strong>Import Data:</strong> Use &quot;Trigger &amp; Monitor&quot; to process files and view integrated progress</li>
                  <li><strong>Monitor Progress:</strong> View running jobs directly in the Dashboard Overview tab</li>
                </ol>
              </div>
            </section>

            {/* Data Source Actions */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">🔧 Data Source Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <Play className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-neutral-900">Trigger & Monitor</h4>
                      <p className="text-sm text-neutral-600">
                        Starts a data import job and immediately opens the live progress monitor. Watch real-time 
                        progress including stages, percentages, and record counts as data is processed.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-neutral-900">View Logs</h4>
                      <p className="text-sm text-neutral-600">
                        Opens detailed logs for this data source, showing job history, execution details, 
                        and any errors. Logs are automatically archived and easily searchable.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Pause className="w-4 h-4 text-yellow-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-neutral-900">Pause/Resume</h4>
                      <p className="text-sm text-neutral-600">
                        Toggles the data source status between Active and Paused. Paused sources will not 
                        run scheduled jobs or process new data until resumed.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <Pause className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-neutral-900">Stop Running Jobs</h4>
                      <p className="text-sm text-neutral-600">
                        Cancel any running jobs for this data source immediately. The job status will 
                        be updated to &quot;cancelled&quot; and can be reviewed in the Job History tab.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-neutral-900">Job History</h4>
                      <p className="text-sm text-neutral-600">
                        Browse complete job execution history with detailed logs, timestamps, 
                        success/failure status, and downloadable log files for debugging.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Upload className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-neutral-900">File Import</h4>
                      <p className="text-sm text-neutral-600">
                        Import individual files directly into the system. Supports CSV, Excel, JSON, 
                        and TXT formats with automatic data validation and error reporting.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* File Management */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">📁 File Management</h2>
              <div className="space-y-6">
                <div className="bg-background-secondary rounded-lg p-4">
                  <h4 className="font-semibold text-neutral-900 mb-2">Supported File Types</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>CSV:</strong> Comma-separated values files for tabular data</li>
                    <li><strong>Excel:</strong> .xlsx and .xls files for spreadsheet data</li>
                    <li><strong>JSON:</strong> JavaScript Object Notation for structured data</li>
                    <li><strong>TXT:</strong> Plain text files for simple data</li>
                  </ul>
                </div>
                
                <div className="bg-background-secondary rounded-lg p-4">
                  <h4 className="font-semibold text-neutral-900 mb-2">File Status Indicators</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span><strong>Green checkmark:</strong> File has been successfully imported</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <File className="w-4 h-4 text-neutral-500" />
                      <span><strong>File icon:</strong> File uploaded but not yet imported</span>
                    </li>
                    <li><strong>&quot;Last imported&quot; timestamp:</strong> Shows when the file was last processed</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Dashboard Overview */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">📊 Dashboard Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-semibold text-neutral-900">Integrated Dashboard</h4>
                  <ul className="space-y-2 text-sm text-neutral-700">
                    <li><strong>Running Jobs Section:</strong> Live progress cards displayed directly in the Dashboard tab</li>
                    <li><strong>Total Sources:</strong> Number of configured data sources</li>
                    <li><strong>Successful Jobs:</strong> Jobs completed without errors in the last 24 hours</li>
                    <li><strong>Failed Jobs:</strong> Jobs that encountered errors and need attention</li>
                    <li><strong>Avg Duration:</strong> Average time to complete import jobs</li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-neutral-900">Real-Time Job Control</h4>
                  <ul className="space-y-2 text-sm text-neutral-700">
                    <li><span className="text-blue-600">🔄 Live Progress:</span> Real-time progress bars with percentages</li>
                    <li><span className="text-green-600">📊 Stage Tracking:</span> Current processing step and record counts</li>
                    <li><span className="text-red-600">🛑 Stop Jobs:</span> Cancel running jobs with one click</li>
                    <li><span className="text-purple-600">📋 Logs:</span> Direct access to job logs and history</li>
                    <li><strong>Auto-refresh:</strong> Updates every 3 seconds automatically</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Streamlined Interface */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">🎯 Streamlined Interface</h2>
              <div className="space-y-4 text-neutral-700">
                <p>
                  The Data Import Management interface has been streamlined to focus on essential features:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-neutral-900">Integrated Progress</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Running jobs displayed directly in Dashboard tab</li>
                      <li>Real-time progress bars with current stages</li>
                      <li>Live record counts and processing steps</li>
                      <li>Immediate job control (stop/cancel) buttons</li>
                      <li>Direct log access for troubleshooting</li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-neutral-900">Available Tabs</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li><strong>Dashboard:</strong> Overview, running jobs, and statistics</li>
                      <li><strong>Data Sources:</strong> Manage and configure import sources</li>
                      <li><strong>Job History:</strong> Complete history with detailed logs</li>
                      <li><strong>Help:</strong> This comprehensive documentation</li>
                      <li><em>Note:</em> Manual Actions tab removed for simplified workflow</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Best Practices */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">💡 Best Practices</h2>
              <div className="space-y-3 text-neutral-700">
                <ul className="list-disc list-inside space-y-2">
                  <li>Use &quot;Trigger &amp; Monitor&quot; to start imports and view integrated progress directly in the Dashboard tab</li>
                  <li>Monitor running jobs in the Dashboard Overview section for real-time status updates</li>
                  <li>Always review file contents before importing to ensure data quality and format compliance</li>
                  <li>Use the Job History tab to track completed imports and identify patterns or issues</li>
                  <li>Use the Stop button judiciously - only cancel jobs when absolutely necessary</li>
                  <li>Access job logs immediately when errors occur for faster troubleshooting</li>
                  <li>Keep file sizes reasonable (under 100MB) for optimal performance and faster imports</li>
                  <li>Use descriptive names for data sources to make management and logs easier to understand</li>
                  <li>Pause data sources temporarily during system maintenance or configuration changes</li>
                  <li>Regularly review the Job History tab to maintain data quality and identify optimization opportunities</li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Links */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/admin/data-import" className="text-primary-600 hover:text-primary-700 text-sm">
                    → Data Import Dashboard
                  </Link>
                </li>
                <li>
                  <Link to="/admin" className="text-primary-600 hover:text-primary-700 text-sm">
                    → Admin Tools
                  </Link>
                </li>
                <li>
                  <Link to="/dashboard" className="text-primary-600 hover:text-primary-700 text-sm">
                    → Main Dashboard
                  </Link>
                </li>
              </ul>
            </div>

            {/* Common Issues */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Common Issues</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <h4 className="font-medium text-neutral-900">Import Fails</h4>
                  <p className="text-neutral-600">Check Dashboard tab for error details and click &quot;Logs&quot; button for complete failure information. Verify file format and data structure.</p>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900">Job Stuck/No Progress</h4>
                  <p className="text-neutral-600">Use the &quot;Stop&quot; button in Dashboard to cancel stuck jobs. Check Job History for detailed error logs.</p>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900">Slow Performance</h4>
                  <p className="text-neutral-600">Monitor real-time progress in Dashboard tab to identify bottlenecks. Consider reducing file size or splitting large imports.</p>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900">Dashboard Not Updating</h4>
                  <p className="text-neutral-600">Progress refreshes automatically every 3 seconds. If issues persist, refresh browser and check Job History tab.</p>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900">Missing Data</h4>
                  <p className="text-neutral-600">Verify job completed successfully in Dashboard or Job History. Check logs for import warnings or data validation issues.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataImportHelpPage; 